import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateCoverageInput, CreateMaterialInput, CreateServiceTypeInput, UpdateCoverageInput,
  UpdateMaterialInput, UpdatePricingRulesInput, UpdateServiceTypeInput, UpdateSizeMultiplierInput,
} from '@wrapforce/shared';
import type { SizeClass } from '@wrapforce/shared/constants';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

function useInvalidateCatalog(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'catalog'] });
}

export function useCreateServiceType(workspaceId: string | null) {
  const invalidate = useInvalidateCatalog(workspaceId);
  return useMutation({
    mutationFn: (input: CreateServiceTypeInput) => api.post<{ id: string }>(workspacePath(workspaceId!, '/catalog/service-types'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateServiceType(workspaceId: string | null) {
  const invalidate = useInvalidateCatalog(workspaceId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateServiceTypeInput }) => api.patch(workspacePath(workspaceId!, `/catalog/service-types/${id}`), input),
    onSuccess: invalidate,
  });
}

export function useCreateCoverage(workspaceId: string | null) {
  const invalidate = useInvalidateCatalog(workspaceId);
  return useMutation({
    mutationFn: ({ serviceTypeId, input }: { serviceTypeId: string; input: CreateCoverageInput }) =>
      api.post<{ id: string }>(workspacePath(workspaceId!, `/catalog/service-types/${serviceTypeId}/coverages`), input),
    onSuccess: invalidate,
  });
}

export function useUpdateCoverage(workspaceId: string | null) {
  const invalidate = useInvalidateCatalog(workspaceId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCoverageInput }) => api.patch(workspacePath(workspaceId!, `/catalog/coverages/${id}`), input),
    onSuccess: invalidate,
  });
}

export function useCreateMaterial(workspaceId: string | null) {
  const invalidate = useInvalidateCatalog(workspaceId);
  return useMutation({
    mutationFn: ({ serviceTypeId, input }: { serviceTypeId: string; input: CreateMaterialInput }) =>
      api.post<{ id: string }>(workspacePath(workspaceId!, `/catalog/service-types/${serviceTypeId}/materials`), input),
    onSuccess: invalidate,
  });
}

export function useUpdateMaterial(workspaceId: string | null) {
  const invalidate = useInvalidateCatalog(workspaceId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateMaterialInput }) => api.patch(workspacePath(workspaceId!, `/catalog/materials/${id}`), input),
    onSuccess: invalidate,
  });
}

export function useUpdatePricingRules(workspaceId: string | null) {
  const invalidate = useInvalidateCatalog(workspaceId);
  return useMutation({
    mutationFn: (input: UpdatePricingRulesInput) => api.patch(workspacePath(workspaceId!, '/catalog/pricing-rules'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateSizeMultiplier(workspaceId: string | null) {
  const invalidate = useInvalidateCatalog(workspaceId);
  return useMutation({
    mutationFn: ({ sizeClass, input }: { sizeClass: SizeClass; input: UpdateSizeMultiplierInput }) =>
      api.patch(workspacePath(workspaceId!, `/catalog/size-multipliers/${sizeClass}`), input),
    onSuccess: invalidate,
  });
}
